import { ConsentKeys, useConsent, PostConsentPayload } from '@carvana/core-consent';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import UserInitializationContext from '|/context/user-initialization-context';
import { useIsInBucket } from '|/helpers/experiment';
import { ReviewSummaryResponse } from '|/services/purchase-api';

export const useConsentData = (summary: ReviewSummaryResponse | null) => {
  const { fetchConsentHtml, consentHtml, postConsent, completedConsents, fetchCompletedConsents } =
    useConsent();
  const { accessToken } = useContext(UserInitializationContext);
  const userId = accessToken?.user?.sub;

  const [agreementStates, setAgreementStates] = useState({
    [ConsentKeys.NRSF]: false,
    [ConsentKeys.MarylandNRSF]: false,
    [ConsentKeys.Jumio]: false,
    income: false,
    [ConsentKeys.HardPull]: false
  });

  // Experiment buckets for NRSF
  const isInThirdPartyBucket = useIsInBucket('pp-nrsf-finance-saf', 'nrsf-finance-saf');
  const isInFinanceBucket = useIsInBucket(
    'finance-nonrefundable-shipping-fees-dec-2022',
    'nonrefundable-shipping-fees'
  );

  // Stabilize library functions that aren't memoized by the provider
  const wrappedFetchConsentHtml = useMemo(() => fetchConsentHtml, []);
  const wrappedFetchCompletedConsents = useMemo(() => fetchCompletedConsents, []);

  useEffect(() => {
    wrappedFetchConsentHtml();
    if (userId) {
      wrappedFetchCompletedConsents(userId);
    }
  }, [wrappedFetchConsentHtml, wrappedFetchCompletedConsents, userId]);

  const purchaseStartedAt = summary?.cartCreatedDateTimeUtc ?? null;

  const previouslyAdoptedConsents = useMemo(() => {
    if (completedConsents.length === 0 || !purchaseStartedAt) return new Set<string>();

    const adopted = completedConsents.filter(
      c => c.isOptedIn && c.source === 'PurchaseUI' && c.consentedOnDateTime >= purchaseStartedAt
    );
    const keys = new Set<string>();
    for (const c of adopted) {
      keys.add(c.typeName);
      if (c.subTypeName) keys.add(c.subTypeName);
    }
    if (keys.has(ConsentKeys.HardPull)) keys.add('income');
    return keys;
  }, [completedConsents, purchaseStartedAt]);

  useEffect(() => {
    if (previouslyAdoptedConsents.size === 0) return;

    setAgreementStates(prev => {
      const next = { ...prev };
      if (previouslyAdoptedConsents.has(ConsentKeys.Jumio)) next[ConsentKeys.Jumio] = true;
      if (previouslyAdoptedConsents.has(ConsentKeys.HardPull)) next[ConsentKeys.HardPull] = true;
      if (previouslyAdoptedConsents.has(ConsentKeys.NRSF)) next[ConsentKeys.NRSF] = true;
      if (previouslyAdoptedConsents.has(ConsentKeys.MarylandNRSF))
        next[ConsentKeys.MarylandNRSF] = true;
      if (previouslyAdoptedConsents.has('income')) next.income = true;
      return next;
    });
  }, [previouslyAdoptedConsents]);

  // Determine if NRSF applies
  const isNRSF = useMemo(() => {
    const method = summary?.funding?.type;
    if (method === 'ThirdParty' && isInThirdPartyBucket !== null) {
      return isInThirdPartyBucket;
    } else if (method !== 'ThirdParty' && isInFinanceBucket !== null) {
      return isInFinanceBucket;
    }
    return false;
  }, [summary?.funding?.type, isInThirdPartyBucket, isInFinanceBucket]);

  // Check if Maryland NRSF applies
  const shouldDisplayMarylandNRSF = useMemo(() => {
    return (
      isNRSF &&
      (summary?.totals?.shippingFee ?? 0) > 0 &&
      (summary?.customer?.address?.state?.toUpperCase() === 'MD' ||
        summary?.transfer?.address?.state?.toUpperCase() === 'MD')
    );
  }, [isNRSF, summary]);

  // Check if regular NRSF applies
  const shouldDisplayNRSF = useMemo(() => {
    return isNRSF && (summary?.totals?.shippingFee ?? 0) > 0;
  }, [isNRSF, summary]);

  // Check if customer has Carvana financing
  const isCarvanaFinanced = useMemo(() => {
    return (
      summary?.funding?.institution?.toLowerCase() === 'carvana' &&
      summary.funding?.type?.toLowerCase() === 'financing'
    );
  }, [summary]);

  // Update agreement state
  const updateAgreement = useCallback((key: keyof typeof ConsentKeys | string, value: boolean) => {
    setAgreementStates(prev => ({ ...prev, [key]: value }));
  }, []);

  // Check if all required agreements are complete
  const areAgreementsComplete = useCallback(() => {
    // Always require Jumio
    if (!agreementStates[ConsentKeys.Jumio]) return false;

    // Require income and hard pull for Carvana financing
    if (isCarvanaFinanced && (!agreementStates.income || !agreementStates[ConsentKeys.HardPull])) {
      return false;
    }

    // Require NRSF if applicable
    if (shouldDisplayNRSF && !agreementStates[ConsentKeys.NRSF]) return false;

    // Require Maryland NRSF if applicable
    if (shouldDisplayMarylandNRSF && !agreementStates[ConsentKeys.MarylandNRSF]) return false;

    return true;
  }, [agreementStates, isCarvanaFinanced, shouldDisplayNRSF, shouldDisplayMarylandNRSF]);

  // Submit all consent data (matches control version)
  const submitAllConsents = useCallback(async () => {
    const consentArray: PostConsentPayload[] = [
      agreementStates[ConsentKeys.HardPull] &&
        isCarvanaFinanced && {
          consentType: ConsentKeys.HardPull,
          isOptedIn: true,
          source: 'PurchaseUI'
        },
      {
        consentType: ConsentKeys.Jumio,
        isOptedIn: true,
        source: 'PurchaseUI'
      },
      agreementStates[ConsentKeys.NRSF] &&
        shouldDisplayNRSF && {
          consentType: ConsentKeys.NRSF,
          isOptedIn: true,
          source: 'PurchaseUI'
        },
      agreementStates[ConsentKeys.MarylandNRSF] &&
        shouldDisplayMarylandNRSF && {
          consentType: 'NRSFStateSpecific',
          consentSubType: ConsentKeys.MarylandNRSF,
          isOptedIn: true,
          source: 'PurchaseUI'
        }
    ].filter(Boolean) as PostConsentPayload[];

    await postConsent(consentArray);
  }, [
    agreementStates,
    isCarvanaFinanced,
    shouldDisplayNRSF,
    shouldDisplayMarylandNRSF,
    postConsent
  ]);

  return {
    agreementStates,
    updateAgreement,
    areAgreementsComplete,
    submitAllConsents,
    consentHtml,
    shouldDisplayNRSF,
    shouldDisplayMarylandNRSF,
    isCarvanaFinanced,
    isNRSF,
    previouslyAdoptedConsents
  };
};
